// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-55 DR-2 — the v4 "Edit drug" record (owner spec carerx-drug-record-interactive-v4.html),
// replacing the legacy DrugModal. Twelve collapsible sections, a readiness badge, per-section AI
// suggest (accept / edit / deny — every action writes a §15 provenance record), the drug specific
// assessment (§11), and protocol metadata fed from the REAL admin-protocol library (per-drug
// override lines ride premed_overrides / kit_overrides as JSON). Suggestions are never written
// until a human acts (§14.3).

import { useCallback, useEffect, useMemo, useState } from 'react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import type { DrugRefRow, DrugRefClinical, DrugAssessQ, AdminProtocol } from '../../api/client'
import { useProtocolLibrary } from '../../hooks/queries/carerxProtocols'
import DrugProtocolSlot from './drugref/DrugProtocolSlot'
import Box from '@mui/material/Box'
import Notice from '../../components/ui/Notice'
import FORM_SPEC from '../../assets/enrollmentFormSpec.json'
import { orderTokens, overrideOf, withOverride, withoutOption, type OverrideKind } from '../../lib/formSelections'
import { ANALYTES, normalizeAnalytes, isKnownAnalyte, analyteLabel } from '../../lib/analytes'

type Cat = { name: string; color: string | null }
type ColDef = { id: string; field_name: string; field_type: string; dropdown_options: string | null }
type CellPatch = { value_text?: string | null; value_number?: number | null; value_bool?: boolean | null; value_date?: string | null }
type Clin = Omit<DrugRefClinical, 'link_count'>

const OPTS = {
  filter_type: ['not_required', '0.22_micron', '1.2_micron', '5_micron', 'inline_standard'],
  pregnancy_test: ['not_required', 'baseline_only', 'before_each_cycle'],
  rescreen_frequency: ['weekly', 'monthly', 'quarterly', 'semi_annually', 'yearly'],
  care_plan_freq: ['Annually', 'Every 6 months', 'Every 3 months', 'Monthly', 'Every cycle'],
  rate_model: ['not_applicable', 'fixed_duration', 'mg_hr_ladder', 'ml_hr_ladder', 'mg_kg_min_ladder', 'ml_kg_min_ladder', 'sc_per_site'],
  diluent: ['ns_only', 'ns_or_d5w', 'per_ifu', 'not_applicable'],
  gravity: ['yes', 'caution', 'no'],
  nurse_needed: ['yes', 'no', 'tech_only'],
  archetype: ['nurse_administered', 'dispense_only'],
  copay_elig: ['Commercial only', 'Commercial + cash', 'Unknown, verify'],
  copay_status: ['Suggested', 'Confirmed', 'None available'],
  competency: ['Health Provider Required', 'IV infusion', 'SubQ Injection', 'Patient can be taught to self administer'],
}

/** The ladder rate models and the unit their start/increment/max are written in. */
const RATE_UNIT: Record<string, string> = {
  mg_hr_ladder: 'mg/hr', ml_hr_ladder: 'mL/hr', mg_kg_min_ladder: 'mg/kg/min', ml_kg_min_ladder: 'mL/kg/min',
}
// D-CRX-80 PR1: the vocabulary moved to lib/analytes. This file used to keep its own
// LABELS-only copy and store `label.toLowerCase()` as the analyte — 'tb screening (igra)' where
// every consumer matches 'tb_igra'. Labs configured here matched nothing, on every drug, since
// this editor went live. Tokens are the stored identity; labels are display only.
const QTAG_CHIP: Record<string, [string, string, string]> = {
  ldd: ['Manufacturer / LDD', '#fdf0f6', '#a61e6b'], rems: ['REMS', '#fdf0ee', '#c0392b'],
  apple: ['Apple clinical', '#fdf6e3', '#8a6d1f'], label: ['Label', '#eef2fd', '#3b5bdb'],
}
type FlushCon = { text: string; source: string }
type Suggestion = { field?: string; value?: unknown; confidence?: string; source_text?: string | null; note?: string | null; state?: 'accepted' | 'edited' | 'denied' }

const emptyClin: Clin = {
  nurse_needed: null, infusion_site_only: null, requires_enrollment_signature: false, pump_required: false,
  filter_type: null, pregnancy_test: null, competency_requirements: [], observation_minutes: null,
  baseline_analytes: [], rescreen_analytes: [], rescreen_frequency: null, therapy_archetype: 'nurse_administered',
  form_premed_selections: null, form_flush_selections: null, form_kit_selections: null,
  premed_protocol_id: null, premed_overrides: null, kit_protocol_id: null, kit_overrides: null,
  flush_protocol_id: null, flush_overrides: null, pump_type: null, named_nurse_only: false, named_nurse_ids: null,
  rate_model: null, rate_first_start: null, rate_first_increment: null, rate_first_interval_min: null, rate_first_max: null,
  rate_subsequent_start: null, rate_subsequent_increment: null, rate_subsequent_interval_min: null, rate_subsequent_max: null,
  diluent: null, standard_final_volume_ml: null, infusion_duration_min: null, setup_minutes: null, visit_length_override_min: null,
  gravity_permitted: null, gravity_rationale: null, nurse_watchouts: null, kit_required: false, filter_label_text: null,
  no_iv_push: false, dedicated_line: false, protect_from_light: false, sequence_dependent: false,
  resume_50pct_on_reaction: false, no_bolus_flush: false,
  copay_phone: null, copay_url: null, copay_eligibility: null, copay_status: null, flush_constraints: null,
}

function parsePairs(s: string | null): [string, string][] {
  if (!s) return []
  try { const a = JSON.parse(s); return Array.isArray(a) ? a.map(x => Array.isArray(x) ? [String(x[0] ?? ''), String(x[1] ?? '')] as [string, string] : [String(x), '']) : [[s, '']] }
  catch { return s.trim() ? [[s, '']] : [] }
}
function parseFlush(s: string | null): FlushCon[] {
  if (!s) return []
  try { const a = JSON.parse(s); return Array.isArray(a) ? a.map((x: any) => ({ text: String(x?.text ?? x ?? ''), source: String(x?.source ?? 'apple_policy') })).filter(f => f.text) : [] }
  catch { return [] }
}

export default function DrugRecordEditV4({ row, cats, cols, cells, clinical, busy, readOnly = false, onClose, onSave, onCell }: {
  row: Partial<DrugRefRow>; cats: Cat[]; cols: ColDef[]; cells: Record<string, string | number | boolean | null>
  clinical: DrugRefClinical | null; busy: boolean; onClose: () => void
  /** Not a pharmacy admin: every write behind this record (save, AI suggest, assessment, protocols,
   *  custom cells) is admin-only on the server, so the record opens for reading only. */
  readOnly?: boolean
  onSave: (r: Partial<DrugRefRow>, clinical?: Clin) => void
  onCell: (fieldDefId: string, patch: CellPatch) => void
}) {
  const [r, setR] = useState<Partial<DrugRefRow>>({ ...row })
  const [c, setC] = useState<Clin>({ ...emptyClin, ...(clinical ?? {}) })
  const [open, setOpen] = useState<Record<string, boolean>>({ identity: true, assess: true })
  const [toast, setToast] = useState('')
  const say = (m: string) => { setToast(m); setTimeout(() => setToast(''), 2400) }

  const [assess, setAssess] = useState<DrugAssessQ[]>([])
  const [prov, setProv] = useState<api.DrugProvRow[]>([])
  const [showAudit, setShowAudit] = useState(false)
  const [spl, setSpl] = useState('')
  const [ai, setAi] = useState<Record<string, { batch: string; items: Suggestion[] } | { msg: string }>>({})
  const [aiBusy, setAiBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!row.id) return
    try {
      const [a, p] = await Promise.all([api.listDrugRefAssessment(row.id), api.listDrugRefProvenance(row.id)])
      setAssess(a.items); setProv(p.items)
    } catch { /* unlinked drug — sections still render */ }
  }, [row.id])
  useEffect(() => { void load() }, [load])
  // D-CRX-89: the library through TanStack Query, so a protocol created / edited / retired from a slot below
  // refreshes every picker at once (E-02).
  const library = useProtocolLibrary()
  const protocols = useMemo(() => library.data?.protocols ?? [], [library.data])
  const components = library.data?.components ?? []

  const premeds = useMemo(() => protocols.filter(p => p.category === 'premedication' && p.active), [protocols])
  const kits = useMemo(() => protocols.filter(p => p.category === 'anaphylaxis_kit' && p.active), [protocols])
  const premedAdds = useMemo(() => parsePairs(c.premed_overrides), [c.premed_overrides])
  const kitAdds = useMemo(() => parsePairs(c.kit_overrides), [c.kit_overrides])
  const flushCons = useMemo(() => parseFlush(c.flush_constraints), [c.flush_constraints])
  const isCare = c.therapy_archetype !== 'dispense_only'
  const unit = RATE_UNIT[c.rate_model ?? ''] ?? ''
  const ladder = unit !== ''

  const provFor = (field: string) => prov.find(p => p.field === field)
  const provLine = (field: string) => {
    const p = provFor(field); if (!p) return null
    const label = { ai_accepted: 'Accepted from AI', ai_edited: 'AI suggestion edited', denied: 'AI suggestion denied', manual: 'Entered manually' }[p.action] ?? p.action
    return <div style={{ fontSize: 10.5, color: p.action === 'manual' || p.action === 'denied' ? 'var(--color-text-soft)' : '#0a7a1d', marginTop: 2 }}><b>{label}</b> · {new Date(p.acted_at).toLocaleString()}</div>
  }
  const record = async (field: string, action: string, after?: string, confidence?: string, source?: string) => {
    if (!row.id) return
    try { await api.addDrugRefProvenance(row.id, { field, action, value_after: after, confidence, source_text: source }); setProv(await api.listDrugRefProvenance(row.id).then(x => x.items)) } catch { /* provenance is best-effort pre-link */ }
  }

  // Visit length (v4 calcVisit)
  const visitMin = (c.visit_length_override_min ?? 0) || ((c.infusion_duration_min ?? 0) + (c.setup_minutes ?? 0) + (c.observation_minutes ?? 0))
  const visitTxt = visitMin ? `${Math.floor(visitMin / 60)}h ${String(visitMin % 60).padStart(2, '0')}m` : '—'

  // Readiness (v4)
  const gaps: string[] = []
  if (isCare) {
    if (!r.gcn && !r.ndc) gaps.push('no identifier')
    if (!r.copay_program && c.copay_status !== 'None available') gaps.push('copay unconfirmed')
    if (!assess.length) gaps.push('no assessment defined')
    if (!c.kit_protocol_id && c.kit_required) gaps.push('kit undefined')
  }
  const readyBadge = !isCare
    ? <span style={{ ...badge, background: '#e9f7ec', color: '#0a7a1d' }}>dispense only</span>
    : !gaps.length ? <span style={{ ...badge, background: '#e9f7ec', color: '#0a7a1d' }}>ready</span>
    : gaps.length <= 1 ? <span style={{ ...badge, background: '#fdf6e3', color: '#8a6d1f' }}>partial · {gaps[0]}</span>
    : <span style={{ ...badge, background: '#fdf0ee', color: '#c0392b' }}>not set up · {gaps.length} gaps</span>

  // ── AI (per prompt doc — suggestions render only; accept/deny writes value + provenance) ──
  const runAI = async (sec: string) => {
    if (!row.id) { say('Save the drug once first'); return }
    setAiBusy(sec)
    try {
      const res = await api.aiSuggestDrugRef(row.id, sec === 'labs' ? 'labs' : sec, spl || undefined)
      setAi(a => ({ ...a, [sec]: { batch: new Date(res.batch_at).toLocaleString(), items: (res.suggestions as Suggestion[]) } }))
      setOpen(o => ({ ...o, [sec]: true }))
    } catch (e) {
      const b = (e as { body?: { error?: string; message?: string } }).body
      setAi(a => ({ ...a, [sec]: { msg: b?.message ?? b?.error ?? 'AI call failed' } }))
      setOpen(o => ({ ...o, [sec]: true }))
    } finally { setAiBusy(null) }
  }
  const applyField = (field: string, value: unknown) => {
    const v = value as string
    switch (field) {
      case 'filter_type': setC(x => ({ ...x, filter_type: v })); break
      case 'filter_label_text': setC(x => ({ ...x, filter_label_text: v })); break
      case 'observation_minutes': setC(x => ({ ...x, observation_minutes: parseInt(String(value)) || null })); break
      case 'pregnancy_test': setC(x => ({ ...x, pregnancy_test: v })); break
      case 'pump_required': setC(x => ({ ...x, pump_required: v === 'Yes' })); break
      case 'care_plan_frequency': setR(x => ({ ...x, care_plan_frequency: v })); break
      case 'rescreen_frequency': setC(x => ({ ...x, rescreen_frequency: v })); break
      case 'baseline_analytes': case 'rescreen_analytes': {
        // D-CRX-80 PR1: an accepted AI suggestion arrives as DISPLAY text ("TB screening (IGRA)
        // · CBC with differential"). Lower-casing it stored a label; normalizeAnalytes maps it to
        // the token the lab tracker and the enrollment door actually match on.
        const arr = Array.isArray(value) ? (value as string[]) : String(value).split('·').map(s => s.trim())
        const toks = normalizeAnalytes(arr)
        setC(x => field === 'baseline_analytes' ? { ...x, baseline_analytes: toks } : { ...x, rescreen_analytes: toks }); break
      }
      case 'premed_protocol_code': {
        const p = premeds.find(pp => pp.code === v); if (p) setC(x => ({ ...x, premed_protocol_id: p.id })); break
      }
      case 'kit_required': setC(x => ({ ...x, kit_required: v === 'Yes' })); break
      case 'rate_model': setC(x => ({ ...x, rate_model: v })); break
      case 'diluent': setC(x => ({ ...x, diluent: v })); break
      case 'standard_final_volume_ml': setC(x => ({ ...x, standard_final_volume_ml: parseInt(String(value)) || null })); break
      case 'infusion_duration_min': setC(x => ({ ...x, infusion_duration_min: parseInt(String(value)) || null })); break
      case 'rate_first_start': setC(x => ({ ...x, rate_first_start: Number(value) || null })); break
      case 'rate_first_increment': setC(x => ({ ...x, rate_first_increment: Number(value) || null })); break
      case 'rate_first_interval_min': setC(x => ({ ...x, rate_first_interval_min: parseInt(String(value)) || null })); break
      case 'rate_first_max': setC(x => ({ ...x, rate_first_max: Number(value) || null })); break
      case 'rate_subsequent_start': setC(x => ({ ...x, rate_subsequent_start: Number(value) || null })); break
      case 'rate_subsequent_increment': setC(x => ({ ...x, rate_subsequent_increment: Number(value) || null })); break
      case 'rate_subsequent_interval_min': setC(x => ({ ...x, rate_subsequent_interval_min: parseInt(String(value)) || null })); break
      case 'rate_subsequent_max': setC(x => ({ ...x, rate_subsequent_max: Number(value) || null })); break
      case 'sequence_dependent': setC(x => ({ ...x, sequence_dependent: value === true || v === 'true' })); break
      case 'no_iv_push': setC(x => ({ ...x, no_iv_push: value === true })); break
      case 'dedicated_line': setC(x => ({ ...x, dedicated_line: value === true })); break
      case 'protect_from_light': setC(x => ({ ...x, protect_from_light: value === true })); break
      case 'resume_50pct_on_reaction': setC(x => ({ ...x, resume_50pct_on_reaction: value === true })); break
      case 'gravity_permitted': setC(x => ({ ...x, gravity_permitted: v })); break
      case 'gravity_rationale': setC(x => ({ ...x, gravity_rationale: v })); break
      case 'nurse_watchouts': setC(x => ({ ...x, nurse_watchouts: v })); break
      case 'flush_constraints': {
        const arr = Array.isArray(value) ? value as Array<{ text?: string; source_text?: string }> : []
        setC(x => ({ ...x, flush_constraints: JSON.stringify(arr.map(e => ({ text: e.text ?? '', source: 'drug_label' })).filter(e => e.text)) })); break
      }
      default: say(`"${field}" has no editable home yet — noted only`)
    }
  }
  const aiPanel = (sec: string) => {
    const st = ai[sec]; if (!st) return null
    if ('msg' in st) return <div style={aip}><b style={aih}>AI suggestion</b><div style={{ fontSize: 12, color: '#8a6d1f', marginTop: 4 }}>{st.msg}</div></div>
    if (!st.items.length) return <div style={aip}><b style={aih}>AI suggestion</b><div style={hint}>Nothing to propose. The label is silent, or these values are policy rather than label content.</div></div>
    return (
      <div style={aip}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 7 }}><b style={aih}>AI suggestions · batch {st.batch}</b>
          <span style={{ ...chip, background: '#f4f0fe', color: '#6741d9' }}>{st.items.length} proposed</span></div>
        {st.items.map((s, i) => {
          const conf = s.confidence ?? 'VERIFY'
          const noVal = s.value === null || s.value === undefined
          return (
            <div key={i} style={{ ...sug, ...(s.state === 'denied' ? { opacity: .55 } : s.state ? { background: '#e9f7ec', borderColor: '#b9e3c3' } : {}) }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 10.5, color: 'var(--color-text-soft)', fontWeight: 700, textTransform: 'uppercase' }}>{s.field}</div>
                <div style={{ fontWeight: 600, fontSize: 12.5 }}>{noVal ? <span style={{ color: '#c0392b' }}>no value — {conf}</span> : typeof s.value === 'object' ? JSON.stringify(s.value).slice(0, 120) : String(s.value)}</div>
                {(s.source_text || s.note) && <div style={{ fontSize: 10.5, color: 'var(--color-text-soft)', fontStyle: 'italic', marginTop: 3 }}>{s.source_text ?? s.note}</div>}
              </div>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <span style={{ ...chip, ...(conf === 'High' ? { background: '#e9f7ec', color: '#0a7a1d' } : conf === 'Medium' ? { background: '#fdf6e3', color: '#8a6d1f' } : { background: '#fdf0ee', color: '#c0392b' }) }}>{conf}</span>
                {!s.state && <>
                  <button style={btnSm} disabled={noVal} title={noVal ? 'No value proposed — enter it manually' : ''} onClick={() => {
                    applyField(s.field!, s.value); void record(s.field!, 'ai_accepted', String(s.value), conf, s.source_text ?? undefined)
                    setAi(a => { const cur = a[sec] as { batch: string; items: Suggestion[] }; cur.items[i] = { ...s, state: 'accepted' }; return { ...a, [sec]: { ...cur } } })
                    say('Accepted · recorded')
                  }}>Accept</button>
                  <button style={btnSm} onClick={() => {
                    const v = prompt('Edit before accepting:', noVal ? '' : String(s.value)); if (v === null) return
                    applyField(s.field!, v); void record(s.field!, 'ai_edited', v, conf, s.source_text ?? undefined)
                    setAi(a => { const cur = a[sec] as { batch: string; items: Suggestion[] }; cur.items[i] = { ...s, value: v, state: 'edited' }; return { ...a, [sec]: { ...cur } } })
                    say('Edited and accepted · recorded')
                  }}>Edit</button>
                  <button style={btnSm} onClick={() => {
                    void record(s.field!, 'denied', noVal ? undefined : String(s.value), conf, s.source_text ?? undefined)
                    setAi(a => { const cur = a[sec] as { batch: string; items: Suggestion[] }; cur.items[i] = { ...s, state: 'denied' }; return { ...a, [sec]: { ...cur } } })
                    say('Denied · recorded')
                  }}>Deny</button>
                </>}
                {s.state && <span style={{ ...chip, background: s.state === 'denied' ? '#f1f3f5' : '#e9f7ec', color: s.state === 'denied' ? 'var(--color-text-soft)' : '#0a7a1d' }}>{s.state}</span>}
              </div>
            </div>
          )
        })}
        <div style={hint}>Nothing is written until you act. Every action is recorded with who and when.</div>
      </div>
    )
  }

  // ── assessment AI (accept adds via the real endpoint) ──
  const aiAssessPanel = () => {
    const st = ai['assess']; if (!st) return null
    if ('msg' in st) return <div style={aip}><b style={aih}>AI suggestion</b><div style={{ fontSize: 12, color: '#8a6d1f', marginTop: 4 }}>{st.msg}</div></div>
    if (!st.items.length) return <div style={aip}><b style={aih}>AI suggestion</b><div style={hint}>No further questions proposed for this drug.</div></div>
    return (
      <div style={aip}>
        <b style={aih}>AI proposed {st.items.length} question{st.items.length === 1 ? '' : 's'} · batch {st.batch}</b>
        {st.items.map((q0, i) => {
          const q = q0 as unknown as { text?: string; type?: string; options?: string[]; expected_answer?: string; out_of_expected?: string; answered_by?: string; asked_when?: string; required?: boolean; tag?: string; confidence?: string; source_text?: string; state?: string }
          const tagc = QTAG_CHIP[q.tag ?? 'apple'] ?? QTAG_CHIP.apple
          return (
            <div key={i} style={{ border: '1px solid #d6c8f7', borderRadius: 6, marginBottom: 7, background: q.state ? (q.state === 'denied' ? '#f4f6f7' : '#e9f7ec') : '#f4f0fe', padding: '8px 11px' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <b style={{ fontSize: 12.5, flex: 1, minWidth: 200 }}>{q.text}</b>
                <span style={{ ...chip, background: tagc[1], color: tagc[2] }}>{tagc[0]}</span>
                <span style={{ ...chip, background: '#f4f0fe', color: '#6741d9' }}>AI · {q.confidence ?? 'VERIFY'}</span>
                {!q.state && <>
                  <button style={btnSm} onClick={async () => {
                    if (!row.id) return
                    try {
                      await api.addDrugRefAssessment(row.id, { question_text: q.text ?? '', question_type: q.type ?? 'Free text', options: q.options, expected_answer: q.expected_answer, out_of_expected: q.out_of_expected, answered_by: q.answered_by, asked_when: q.asked_when, required: q.required, tag: q.tag, origin: 'ai', action: 'ai_accepted', confidence: q.confidence, source_text: q.source_text })
                      await load(); (q as { state?: string }).state = 'accepted'; setAi(a => ({ ...a })); say('Question added · recorded')
                    } catch (e) { say((e as { body?: { message?: string } }).body?.message ?? 'Add failed') }
                  }}>Accept</button>
                  <button style={btnSm} onClick={() => { void record('assessment_question', 'denied', q.text); (q as { state?: string }).state = 'denied'; setAi(a => ({ ...a })); say('Denied · recorded') }}>Deny</button>
                </>}
                {q.state && <span style={{ ...chip, background: '#e9f7ec', color: '#0a7a1d' }}>{q.state}</span>}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--color-text-soft)', marginTop: 4 }}>
                {q.type} · {q.answered_by} · {q.asked_when} · expected: <b>{q.expected_answer}</b> · if outside: {q.out_of_expected}
              </div>
              {q.source_text && <div style={{ fontSize: 10.5, fontStyle: 'italic', color: 'var(--color-text-soft)', marginTop: 3 }}>{q.source_text}</div>}
            </div>
          )
        })}
      </div>
    )
  }

  // ── section shell ──
  // SecBase and F live at MODULE scope (see the note at the bottom of this file). What they need
  // from this closure is passed as PROPS — deliberately not by wrapping them in a component
  // declared here, which would recreate the type on every render and remount the whole form.
  // Read-only: no AI suggest button and no suggestion panel (both write provenance, admin-only).
  const secProps = (id: string, sectionAi?: string) => {
    const aiKey = readOnly ? undefined : sectionAi
    return {
      id, aiKey,
      isOpen: !!open[id],
      onToggle: () => setOpen(o => ({ ...o, [id]: !o[id] })),
      aiBusy,
      onAI: runAI,
      panel: aiKey === 'assess' ? aiAssessPanel() : aiKey ? aiPanel(aiKey) : null,
    }
  }
  const sel = (v: string | null | undefined, opts: string[], set: (x: string) => void) => (
    <SearchableSelect value={v ?? ''} onChange={set} style={inp}
      options={[{ value: '', label: '—' }, ...opts.map(o => ({ value: o, label: o }))]} />
  )
  // The three enrollment-form sections, read straight off the spec the PDF is generated from.
  type SelField = 'form_premed_selections' | 'form_flush_selections' | 'form_kit_selections'
  // qty / sig are the spec's defaults for an order that prints with them (§07 kit, go-live #8).
  const FORM_SECTIONS: { field: SelField; title: string; items: { key: string; label: string; children: [string, string][]; freeText?: string; qty?: string; sig?: string }[] }[] = useMemo(() => {
    const bySection = (no: string) => (FORM_SPEC.sections as any[]).find(x => x.no === no)?.checklist ?? []
    const map = (no: string) => bySection(no).map((i: any) => ({
      key: i.key, label: i.label, children: (i.children ?? []) as [string, string][], freeText: i.freeText as string | undefined,
      qty: (i.qty || undefined) as string | undefined, sig: (i.body || undefined) as string | undefined }))
    return [
      { field: 'form_premed_selections', title: '\u00a705 Premedication orders', items: map('05') },
      { field: 'form_flush_selections', title: '\u00a706 Flushing protocol', items: map('06') },
      { field: 'form_kit_selections', title: '\u00a707 Anaphylaxis kit', items: map('07') },
    ]
  }, [])
  // Stored as JSON; tolerate an array or a string so an older payload never breaks the dialog.
  const readSel = (field: SelField): string[] => {
    const v = (c as any)[field]
    if (Array.isArray(v)) return v
    if (typeof v === 'string' && v.trim()) { try { const p = JSON.parse(v); return Array.isArray(p) ? p : [] } catch { return [] } }
    return []
  }
  const hasSel = (field: SelField, key: string) => readSel(field).includes(key)
  const selCount = (field: SelField) => orderTokens(readSel(field)).length
  const toggleSel = (field: SelField, key: string, clearsFreeText = false) => {
    const cur = readSel(field)
    const off = cur.includes(key)
    // Unticking takes the option's qty / sig overrides with it: an override with no tick is no order.
    let next = off ? withoutOption(cur, key) : [...cur, key]
    // Un-ticking "Other" takes its text with it: text with no box would print an order nobody ordered.
    if (off && clearsFreeText) next = next.filter(k => !k.startsWith('other:'))
    setC(x => ({ ...x, [field]: next.length ? next : null }) as typeof x)
  }
  // "Other" is what the form's fixed options cannot say. It rides in the SAME array as
  // `other:<free text>` — no second column, no shape change, and the PDF filler ticks the
  // section's Other box and prints the remainder on its line.
  // Go-live #8: the quantity and sig an order prints with. A blank field prints the spec's default,
  // which it shows as its placeholder; typing the default back stores nothing either.
  const overrideText = (field: SelField, kind: OverrideKind, key: string) => overrideOf(readSel(field), kind, key) ?? ''
  const setOverride = (field: SelField, kind: OverrideKind, key: string, text: string, fallback?: string) => {
    const next = withOverride(readSel(field), kind, key, text, fallback)
    setC(x => ({ ...x, [field]: next.length ? next : null }) as typeof x)
  }
  const OTHER = 'other:'
  const otherText = (field: SelField) => readSel(field).find(k => k.startsWith(OTHER))?.slice(OTHER.length) ?? null
  const setOther = (field: SelField, text: string | null) => {
    const rest = readSel(field).filter(k => !k.startsWith(OTHER))
    const next = text === null ? rest : [...rest, OTHER + text]
    setC(x => ({ ...x, [field]: next.length ? next : null }) as typeof x)
  }

  const protoLines = (p: AdminProtocol | undefined, adds: [string, string][], kind: 'premed' | 'kit') => {
    if (!p) return <div style={hint}>No protocol assigned.</div>
    return (
      <div>
        {p.lines.map((l, i) => (
          <div key={i} style={ln}><input type="checkbox" defaultChecked readOnly /><span style={{ flex: 1 }}><span>{l.item ?? '—'}</span>
            {l.source && <span style={{ ...srcTag }}>{l.source}</span>}
            {l.sig && <div style={{ fontSize: 11.5, color: 'var(--color-text-soft)' }}>{l.sig}</div>}</span></div>
        ))}
        {adds.map((l, i) => (
          <div key={'a' + i} style={ln}><input type="checkbox" defaultChecked readOnly /><span style={{ flex: 1 }}>
            <span style={{ color: '#0a7a1d', fontWeight: 600 }}>{l[0]}</span> <span style={{ ...chip, background: '#eef2fd', color: '#3b5bdb' }}>added for this drug</span>
            {l[1] && <div style={{ fontSize: 11.5, color: 'var(--color-text-soft)' }}>{l[1]}</div>}</span>
            {!readOnly && <span style={{ color: '#c0392b', cursor: 'pointer' }} onClick={() => {
              const next = adds.filter((_, j) => j !== i)
              setC(x => kind === 'premed' ? { ...x, premed_overrides: next.length ? JSON.stringify(next) : null } : { ...x, kit_overrides: next.length ? JSON.stringify(next) : null })
            }}>×</span>}</div>
        ))}
        <div style={hint}>Following <b>{p.code} {p.name}</b> v{p.version} · {adds.length ? `differs on ${adds.length} line${adds.length > 1 ? 's' : ''}` : 'no changes'}
          {/* Links are not disabled by the read-only fieldset, and this one writes provenance. */}
          {!readOnly && <>{' · '}<a href="#" style={{ color: '#3b5bdb' }} onClick={e => {
            e.preventDefault(); const t = prompt('Line to add for this drug only:'); if (!t) return
            const sg = prompt('Sig (optional):') ?? ''
            const next = [...adds, [t, sg] as [string, string]]
            setC(x => kind === 'premed' ? { ...x, premed_overrides: JSON.stringify(next) } : { ...x, kit_overrides: JSON.stringify(next) })
            void record(kind + '_override', 'manual', t)
          }}>Add line</a></>}</div>
      </div>
    )
  }
  const premedP = premeds.find(p => p.id === c.premed_protocol_id)
  const kitP = kits.find(p => p.id === c.kit_protocol_id)
  const flushP = protocols.find(p => p.category === 'flushing' && p.id === c.flush_protocol_id)

  // D-CRX-80 PR1: takes the TOKEN, never the label. The list is normalized on the way in as
  // well, so a record still holding V4's label-shaped values is repaired the first time it is
  // saved — without any tenant-data write of our own.
  const toggleTok = (kind: 'baseline' | 'rescreen', token: string) => {
    setC(x => {
      const arr = normalizeAnalytes(kind === 'baseline' ? x.baseline_analytes : x.rescreen_analytes)
      const i = arr.indexOf(token); if (i >= 0) arr.splice(i, 1); else arr.push(token)
      return kind === 'baseline' ? { ...x, baseline_analytes: arr } : { ...x, rescreen_analytes: arr }
    })
  }
  const [labSeg, setLabSeg] = useState<'baseline' | 'rescreen'>('baseline')

  const save = () => onSave(r, c)

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,28,34,.45)', zIndex: 60, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 10px', overflow: 'auto' }} onClick={onClose}>
      <div style={{ background: 'var(--color-surface, #fff)', borderRadius: 8, maxWidth: '100%', width: '100%', boxShadow: '0 4px 20px rgba(0,0,0,.2)', margin: 'auto 0' }} onClick={e => e.stopPropagation()}>
        {/* top bar */}
        <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', gap: 11, alignItems: 'center', flexWrap: 'wrap' }}>
          <b style={{ fontSize: 15 }}>{readOnly ? 'View drug' : 'Edit drug'}</b>
          <span style={{ fontSize: 13 }}>{r.medication_name || r.generic_name || 'New drug'}</span>
          <span style={{ ...chip, background: '#e9f7ec', color: '#0a7a1d' }}>CareRx clinical · {clinical?.link_count ?? 0} linked formulary row{(clinical?.link_count ?? 0) === 1 ? '' : 's'}</span>
          {!readOnly && <button style={{ ...btnSm, background: '#6741d9', borderColor: '#6741d9', color: '#fff' }} disabled={aiBusy !== null || !row.id}
            onClick={() => { ['clinical', 'labs', 'protocols', 'flushcon', 'rate', 'duration', 'constraints', 'assess'].forEach(s => void runAI(s)) }}>Suggest everything</button>}
          <span style={{ marginLeft: 'auto' }}>{readyBadge}</span>
        </div>
        {readOnly ? (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Notice severity="info">Read-only. Changing this drug, its labs or its protocols needs a pharmacy admin.</Notice>
          </Box>
        ) : (
          // SPL input for the AI batch (the doc's §2 'what to send' — trimmed label text)
          <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-soft, #f7f9fa)' }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#8a6d1f' }}>Label text for AI (trimmed SPL — Dosage & Administration, Warnings, Boxed Warning, How Supplied)</label>
            <textarea value={spl} onChange={e => setSpl(e.target.value)} placeholder="Paste the trimmed label sections here before running AI suggest — SOURCE OR SILENCE: without label text most fields return null/VERIFY."
              style={{ ...inp, minHeight: 44, marginTop: 3 }} />
          </div>
        )}

        {/* sections. Read-only: a disabled fieldset turns off every input, select and button inside
            (section headers are divs, so they still open and close). Supply stays outside it: pick
            lists are staff work on the server. */}
        <fieldset disabled={readOnly} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
        <SecBase {...secProps('identity')} title="Identity" chipEl={<span style={{ ...chip, background: '#e9f7ec', color: '#0a7a1d' }}>from drug_reference</span>} sum={`${r.category ?? '—'} · ${c.therapy_archetype}`}>
          <div style={grid3}>
            <F label="Medication name"><input style={inp} value={r.medication_name ?? ''} onChange={e => setR(x => ({ ...x, medication_name: e.target.value }))} /></F>
            <F label="Generic name"><input style={inp} value={r.generic_name ?? ''} onChange={e => setR(x => ({ ...x, generic_name: e.target.value }))} /></F>
            <F label="Brand name"><input style={inp} value={r.brand_name ?? ''} onChange={e => setR(x => ({ ...x, brand_name: e.target.value }))} /></F>
          </div>
          <div style={grid3}>
            <F label="GCN"><input style={{ ...inp, ...(r.gcn ? {} : warn) }} placeholder="not set" value={r.gcn ?? ''} onChange={e => setR(x => ({ ...x, gcn: e.target.value }))} /></F>
            <F label="NDC"><input style={{ ...inp, ...(r.ndc ? {} : warn) }} placeholder="not set" value={r.ndc ?? ''} onChange={e => setR(x => ({ ...x, ndc: e.target.value }))} /></F>
            <F label="Category">{sel(r.category, cats.map(x => x.name), v => setR(x => ({ ...x, category: v })))}</F>
            <F label="Therapy archetype">{sel(c.therapy_archetype, OPTS.archetype, v => setC(x => ({ ...x, therapy_archetype: v })))}</F>
            <F label="Active">{sel(r.is_active === false ? 'No' : 'Yes', ['Yes', 'No'], v => setR(x => ({ ...x, is_active: v === 'Yes' })))}</F>
          </div>
          {!isCare && <div style={{ ...callout, borderLeftColor: '#c0392b', background: '#fdf0ee' }}><b>Non-CareRx drug.</b> Dispense only. Excluded from the staffing board.</div>}
        </SecBase>

        <SecBase {...secProps('clinical', 'clinical')} title="CareRx clinical" dim={!isCare} sum={`${c.nurse_needed ?? '—'} · ${r.care_plan_frequency ?? 'no cadence'}`}>
          <div style={grid3}>
            <F label="Nurse needed">{sel(c.nurse_needed, OPTS.nurse_needed, v => setC(x => ({ ...x, nurse_needed: v })))}</F>
            <F label="Infusion site only">{sel(c.infusion_site_only == null ? null : c.infusion_site_only ? 'Yes' : 'No', ['Yes', 'No'], v => setC(x => ({ ...x, infusion_site_only: v === 'Yes' })))}</F>
            <F label="Requires enrollment signature">{sel(c.requires_enrollment_signature ? 'Yes' : 'No', ['Yes', 'No'], v => setC(x => ({ ...x, requires_enrollment_signature: v === 'Yes' })))}</F>
            <F label={<>Pump required</>}>{sel(c.pump_required ? 'Yes' : 'No', ['Yes', 'No'], v => setC(x => ({ ...x, pump_required: v === 'Yes' })))}{provLine('pump_required')}</F>
            <F label="Pump type"><input style={inp} value={c.pump_type ?? ''} onChange={e => setC(x => ({ ...x, pump_type: e.target.value }))} /></F>
            <F label="Pregnancy test">{sel(c.pregnancy_test, OPTS.pregnancy_test, v => setC(x => ({ ...x, pregnancy_test: v })))}{provLine('pregnancy_test')}</F>
          </div>
          <div style={grid2}>
            <F label={<>Filter type <span style={{ ...chip, background: '#fdf6e3', color: '#8a6d1f' }}>mapped</span></>}>{sel(c.filter_type, OPTS.filter_type, v => setC(x => ({ ...x, filter_type: v })))}{provLine('filter_type')}</F>
            <F label={<>Filter label text <span style={{ ...chip, background: '#eef2fd', color: '#3b5bdb' }}>never narrowed silently</span></>}><input style={inp} value={c.filter_label_text ?? ''} onChange={e => setC(x => ({ ...x, filter_label_text: e.target.value }))} /></F>
          </div>
          <div style={grid2}>
            <F label="Care plan frequency">{sel(r.care_plan_frequency, OPTS.care_plan_freq, v => setR(x => ({ ...x, care_plan_frequency: v })))}{provLine('care_plan_frequency')}</F>
            <F label="Observation window (min)"><input style={inp} type="number" value={c.observation_minutes ?? ''} onChange={e => setC(x => ({ ...x, observation_minutes: e.target.value === '' ? null : parseInt(e.target.value) }))} />{provLine('observation_minutes')}</F>
          </div>
          <F label="Competency required">
            <div style={cbs}>{OPTS.competency.map(cc => (
              <label key={cc} style={cb}><input type="checkbox" checked={c.competency_requirements.includes(cc)}
                onChange={() => setC(x => ({ ...x, competency_requirements: x.competency_requirements.includes(cc) ? x.competency_requirements.filter(y => y !== cc) : [...x.competency_requirements, cc] }))} /><span>{cc}</span></label>
            ))}</div>
          </F>
        </SecBase>

        <SecBase {...secProps('assess', 'assess')} title="Drug specific assessment" dim={!isCare}
          chipEl={<span style={{ ...chip, background: '#eef2fd', color: '#3b5bdb' }}>loads into Clinical Review</span>}
          sum={`${assess.length} question${assess.length === 1 ? '' : 's'}`}>
          <div style={{ ...callout }}><b>What the manufacturer and LDD contract expect.</b> These load into Clinical Review at <b>initial</b> and then on this drug's care plan frequency, currently <b>{r.care_plan_frequency ?? 'not set'}</b>.</div>
          <div style={{ display: 'flex', marginBottom: 8 }}>
            <span style={hint}>{assess.length} question{assess.length === 1 ? '' : 's'} · {assess.filter(q => q.required).length} required</span>
            <button style={{ ...btnSm, marginLeft: 'auto', background: 'var(--color-primary)', borderColor: 'var(--color-primary)', color: '#fff' }} onClick={async () => {
              if (!row.id) { say('Save the drug once first'); return }
              const t = prompt('Question text:'); if (!t) return
              const type = prompt('Type — Yes / No, Single select, Multi select, Free text, Numeric, Date, Scale or score', 'Single select') ?? 'Single select'
              const optsCsv = ['Single select', 'Multi select', 'Scale or score', 'Yes / No'].includes(type) ? (prompt('Options, comma separated:', '') ?? '') : ''
              const exp = prompt('Expected answer:') ?? ''
              try {
                await api.addDrugRefAssessment(row.id, { question_text: t, question_type: type, options: optsCsv.split(',').map(s => s.trim()).filter(Boolean), expected_answer: exp, origin: 'manual', action: 'manual' })
                await load(); say('Question added manually · recorded')
              } catch (e) { say((e as { body?: { message?: string; error?: string } }).body?.message ?? 'Add failed') }
            }}>Add question manually</button>
          </div>
          {assess.map((q, i) => {
            const tagc = QTAG_CHIP[q.tag] ?? QTAG_CHIP.apple
            const opts = ((): string[] => { try { return q.options ? JSON.parse(q.options) : [] } catch { return [] } })()
            return (
              <div key={q.id} style={{ border: '1px solid var(--color-border)', borderRadius: 6, marginBottom: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 11px', background: 'var(--color-surface-soft, #f7f9fa)', borderBottom: '1px solid var(--color-border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-soft)' }}>Q{i + 1}</span>
                  <b style={{ flex: 1, fontSize: 12.5, minWidth: 180 }}>{q.question_text}</b>
                  <span style={{ ...chip, background: tagc[1], color: tagc[2] }}>{tagc[0]}</span>
                  <span style={{ ...chip, background: q.origin === 'ai' ? '#f4f0fe' : '#f1f3f5', color: q.origin === 'ai' ? '#6741d9' : 'var(--color-text-soft)' }}>{q.origin === 'ai' ? 'AI' : 'manual'}</span>
                  {q.required && <span style={{ ...chip, background: '#e9f7ec', color: '#0a7a1d' }}>required</span>}
                  <button style={btnSm} onClick={async () => {
                    const t = prompt('Question text:', q.question_text); if (t === null) return
                    const e2 = prompt('Expected answer:', q.expected_answer ?? ''); if (e2 === null) return
                    try { await api.updateDrugRefAssessment(q.id, { question_text: t, expected_answer: e2 }); await load(); say('Question edited · recorded') } catch { say('Edit failed') }
                  }}>Edit</button>
                  <button style={btnSm} onClick={async () => { try { await api.removeDrugRefAssessment(q.id); await load(); say('Question removed') } catch { say('Remove failed') } }}>Remove</button>
                </div>
                <div style={{ padding: '9px 12px', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--color-text-soft)' }}>{q.question_type} · {q.answered_by} · {q.asked_when}</span>
                  {opts.length > 0 && <div style={hint}><b>Options:</b> {opts.join(' · ')}</div>}
                  <div style={{ borderLeft: '3px solid var(--color-primary)', background: '#e9f7ec', padding: '5px 9px', marginTop: 5, borderRadius: '0 4px 4px 0' }}>
                    <b style={{ fontSize: 10, textTransform: 'uppercase', color: '#0a7a1d' }}>Expected answer</b><div>{q.expected_answer ?? '—'}</div></div>
                  <div style={{ borderLeft: '3px solid #e8c97a', background: '#fdf6e3', padding: '5px 9px', marginTop: 5, borderRadius: '0 4px 4px 0', fontSize: 11.5 }}>
                    <b>If outside expected:</b> {q.out_of_expected}</div>
                </div>
              </div>
            )
          })}
          <div style={{ ...callout, borderLeftColor: 'var(--color-primary)', background: '#e9f7ec' }}><b>Tags drive the export.</b> Manufacturer / LDD questions go in the manufacturer file, REMS questions report separately, Apple clinical questions never leave. Unanswered required questions warn, they do not block.</div>
        </SecBase>

        <SecBase {...secProps('labs', 'labs')} title="Labs and screening" sum={`${c.baseline_analytes.length} baseline · ${c.rescreen_analytes.length} re-screen`}>
          <div style={{ display: 'inline-flex', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
            {(['baseline', 'rescreen'] as const).map(k => (
              <span key={k} onClick={() => setLabSeg(k)} style={{ padding: '5px 13px', fontSize: 12, cursor: 'pointer', background: labSeg === k ? 'var(--color-primary)' : 'var(--color-surface)', color: labSeg === k ? '#fff' : 'var(--color-text)', fontWeight: labSeg === k ? 600 : 400 }}>
                {k === 'baseline' ? 'Baseline required' : 'Re-screen every cycle'}</span>
            ))}
          </div>
          <div style={cbs}>{ANALYTES.map(a => {
            const on = normalizeAnalytes(labSeg === 'baseline' ? c.baseline_analytes : c.rescreen_analytes).includes(a.token)
            return <label key={a.token} style={cb}><input type="checkbox" checked={on} onChange={() => toggleTok(labSeg, a.token)} /><span>{a.label}</span></label>
          })}
          {/* An analyte this vocabulary does not name — a seeded or hand-entered one — stays
              visible and checked. Hiding it is how a configured lab silently disappears. */}
          {normalizeAnalytes(labSeg === 'baseline' ? c.baseline_analytes : c.rescreen_analytes)
            .filter(t => !isKnownAnalyte(t))
            .map(t => (
              <label key={t} style={{ ...cb, color: 'var(--color-warning-fg, #92400e)' }}
                title="Not in the standard analyte list — uncheck to remove it">
                <input type="checkbox" checked onChange={() => toggleTok(labSeg, t)} /><span>{analyteLabel(t)}</span>
              </label>
            ))}</div>
          {provLine('baseline_analytes')}
          <div style={grid2}>
            <F label="Re-screen frequency">{sel(c.rescreen_frequency, OPTS.rescreen_frequency, v => setC(x => ({ ...x, rescreen_frequency: v })))}</F>
            <div />
          </div>
        </SecBase>

        <SecBase {...secProps('copay')} title="Copay assistance" sum={c.copay_status ?? '—'}
          chipEl={<span style={{ ...chip, background: '#fdf6e3', color: '#8a6d1f' }}>AI needs browsing — manual here</span>}>
          <div style={grid2}>
            <F label="Program name"><input style={inp} placeholder="not set" value={r.copay_program ?? ''} onChange={e => setR(x => ({ ...x, copay_program: e.target.value }))} />{provLine('copay_program')}</F>
            <F label="Phone"><input style={inp} value={c.copay_phone ?? ''} onChange={e => setC(x => ({ ...x, copay_phone: e.target.value }))} /></F>
          </div>
          <div style={grid2}>
            <F label="URL"><input style={inp} value={c.copay_url ?? ''} onChange={e => setC(x => ({ ...x, copay_url: e.target.value }))} /></F>
            <F label="Eligibility">{sel(c.copay_eligibility, OPTS.copay_elig, v => setC(x => ({ ...x, copay_eligibility: v })))}</F>
          </div>
          <div style={grid2}>
            <F label="Status">{sel(c.copay_status, OPTS.copay_status, v => setC(x => ({ ...x, copay_status: v })))}</F><div />
          </div>
          <div style={{ ...callout, borderLeftColor: '#6741d9', background: '#f4f0fe' }}>Never invented. Uncertain means blank and VERIFY. Manufacturer programs exclude Medicare, Medicaid and TRICARE by law.</div>
        </SecBase>

        {/* D-CRX-67 — the enrollment form's own §05/§06/§07 checkbox lists, rendered from the SAME
            spec that draws the PDF, so a box ticked here is the box that prints. Before this the
            drug record held these as prose and the generated form left all three blank, which is
            why the pharmacy re-entered by hand what it had already configured. The protocol
            library below is unchanged: it drives nursing and the plan of treatment, this drives
            the document. */}
        <SecBase {...secProps('formsel', 'formsel')} title="Enrollment form orders" dim={!isCare}
          chipEl={<span style={{ ...chip, background: '#e7f5ec', color: '#1d6b45' }}>prints on the enrollment PDF</span>}
          sum={`${selCount('form_premed_selections')} premed · ${selCount('form_flush_selections')} flush · ${selCount('form_kit_selections')} kit`}>
          <div style={callout}>These are the enrollment form's fixed options (§05 / §06 / §07). What you tick here
            is exactly what the generated PDF ticks — nothing is inferred from the protocol text.</div>
          {FORM_SECTIONS.map(fs => (
            <div key={fs.field} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#41566a', margin: '8px 0 4px' }}>{fs.title}</div>
              {fs.items.map(it => (
                <div key={it.key}>
                  <label style={{ ...ln, cursor: 'pointer' }}>
                    <input type="checkbox" checked={hasSel(fs.field, it.key)} onChange={() => toggleSel(fs.field, it.key, !!it.freeText)} />
                    <span style={{ flex: 1, fontWeight: it.children.length ? 600 : 400 }}>{it.label}</span>
                  </label>
                  {/* An "Other" box is only an order once it says what the order is — the text
                      lives with its own checkbox rather than in a second row of its own. */}
                  {it.freeText && hasSel(fs.field, it.key) && (
                    <div style={{ margin: '0 0 6px 26px' }}>
                      <input value={otherText(fs.field) ?? ''} autoFocus
                        placeholder="What should the form say? e.g. Famotidine 20 mg IV 30 min prior"
                        onChange={e => setOther(fs.field, e.target.value)}
                        style={{ ...inp, width: '100%', fontSize: 12 }} />
                      {!(otherText(fs.field) ?? '').trim() &&
                        <div style={{ ...hint, marginTop: 3 }}>Nothing prints on the form until this says something.</div>}
                    </div>
                  )}
                  {(it.qty || it.sig) && hasSel(fs.field, it.key) && (
                    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 6, margin: '0 0 6px 26px' }}>
                      <input aria-label={`Quantity — ${it.label}`} placeholder={it.qty ?? 'Qty, e.g. #1 vial'}
                        value={overrideText(fs.field, 'qty', it.key)}
                        onChange={e => setOverride(fs.field, 'qty', it.key, e.target.value, it.qty)}
                        style={{ ...inp, fontSize: 12 }} />
                      <input aria-label={`Sig — ${it.label}`} placeholder={it.sig ?? 'Sig as it prints on the form'}
                        value={overrideText(fs.field, 'sig', it.key)}
                        onChange={e => setOverride(fs.field, 'sig', it.key, e.target.value, it.sig)}
                        style={{ ...inp, fontSize: 12 }} />
                    </div>
                  )}
                  {it.children.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '0 0 4px 26px' }}>
                      {it.children.map(([ck, clabel]) => (
                        <label key={ck} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                          <input type="checkbox" checked={hasSel(fs.field, ck)} onChange={() => toggleSel(fs.field, ck)} />
                          {clabel}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </SecBase>

        <SecBase {...secProps('protocols', 'protocols')} title="Protocols" dim={!isCare}
          chipEl={<span style={{ ...chip, background: '#fdf6e3', color: '#8a6d1f' }}>fed from the protocol library</span>}
          sum={`${premedP?.name ?? 'no premed'} · ${kitP?.name ?? 'no kit'} · ${flushP?.name ?? 'no flush'}`}>
          {/* D-CRX-89: these are the drug's PRESETS. Staff can still choose a different protocol for one
              patient at enrollment; that never changes the drug. */}
          {library.isError && <Notice severity="error">The protocol library could not be loaded. <a href="#" onClick={e => { e.preventDefault(); void library.refetch() }}>Retry</a></Notice>}
          <DrugProtocolSlot slot="premed" value={c.premed_protocol_id} savedValue={clinical?.premed_protocol_id ?? null} protocols={protocols} components={components}
            drugReferenceId={row.id ?? null} readOnly={readOnly}
            onChange={id => setC(x => ({ ...x, premed_protocol_id: id, premed_overrides: id === x.premed_protocol_id ? x.premed_overrides : null }))}>
            {protoLines(premedP, premedAdds, 'premed')}
          </DrugProtocolSlot>
          <DrugProtocolSlot slot="kit" value={c.kit_protocol_id} savedValue={clinical?.kit_protocol_id ?? null} protocols={protocols} components={components}
            drugReferenceId={row.id ?? null} readOnly={readOnly}
            onChange={id => setC(x => ({ ...x, kit_protocol_id: id, kit_overrides: id === x.kit_protocol_id ? x.kit_overrides : null }))}>
            {kitP && kitP.lines.length === 0 && <div style={{ ...callout, borderLeftColor: '#c0392b', background: '#fdf0ee' }}><b>Empty shell.</b> Contents and sigs are not defined yet.</div>}
            {protoLines(kitP, kitAdds, 'kit')}
          </DrugProtocolSlot>
          <DrugProtocolSlot slot="flush" value={c.flush_protocol_id} savedValue={clinical?.flush_protocol_id ?? null} protocols={protocols} components={components}
            drugReferenceId={row.id ?? null} readOnly={readOnly}
            onChange={id => setC(x => ({ ...x, flush_protocol_id: id, flush_overrides: id === x.flush_protocol_id ? x.flush_overrides : null }))}>
            {flushP
              ? flushP.lines.map((l, i) => (
                <div key={i} style={ln}><Box component="span" sx={{ flex: 1 }}>{l.item ?? '—'}{l.volume_ml ? ` · ${l.volume_ml} mL` : ''}{l.timing ? ` · ${l.timing}` : ''}
                  {l.sig && <Box sx={{ fontSize: 11.5, color: 'var(--color-text-soft)' }}>{l.sig}</Box>}</Box></div>))
              : <div style={hint}>No protocol assigned.</div>}
          </DrugProtocolSlot>
          {/* "Kit required" removed from this dialog (owner, 2026-08-26). The column and its value
              are untouched — the dialog still round-trips whatever is stored, it just no longer
              offers it here. */}
        </SecBase>

        <SecBase {...secProps('flushcon', 'flushcon')} title="Flush and compatibility constraints" dim={!isCare}
          chipEl={<span style={{ ...chip, background: '#f4f0fe', color: '#6741d9' }}>AI from the PI</span>}
          sum={`${flushCons.length} constraint${flushCons.length === 1 ? '' : 's'}`}>
          <div style={{ ...callout, borderLeftColor: '#6741d9', background: '#f4f0fe' }}>Drug properties, not a flush protocol.</div>
          {flushCons.length === 0 && <div style={hint}>None found in the label.</div>}
          {flushCons.map((f, i) => (
            <div key={i} style={ln}><input type="checkbox" defaultChecked readOnly /><span style={{ flex: 1 }}>{f.text}<span style={srcTag}>{f.source}</span></span>
              {!readOnly && <span style={{ color: '#c0392b', cursor: 'pointer' }} onClick={() => {
                const next = flushCons.filter((_, j) => j !== i)
                setC(x => ({ ...x, flush_constraints: next.length ? JSON.stringify(next) : null }))
              }}>×</span>}</div>
          ))}
          {!readOnly && <div style={hint}><a href="#" style={{ color: '#3b5bdb' }} onClick={e => {
            e.preventDefault(); const t = prompt('Constraint text:'); if (!t) return
            setC(x => ({ ...x, flush_constraints: JSON.stringify([...flushCons, { text: t, source: 'apple_policy' }]) }))
            void record('flush_constraints', 'manual', t)
          }}>Add constraint</a></div>}
        </SecBase>

        <SecBase {...secProps('rate', 'rate')} title="Rate rule" dim={!isCare}
          sum={ladder ? `${c.rate_first_start ?? '—'}→${c.rate_first_max ?? '—'} ${unit}` : c.rate_model ?? '—'}>
          <div style={grid3}>
            <F label="Rate model">{sel(c.rate_model, OPTS.rate_model, v => setC(x => ({ ...x, rate_model: v })))}{provLine('rate_model')}</F>
            <F label="Diluent">{sel(c.diluent, OPTS.diluent, v => setC(x => ({ ...x, diluent: v })))}</F>
            <F label="Standard final volume (mL)"><input style={inp} type="number" value={c.standard_final_volume_ml ?? ''} onChange={e => setC(x => ({ ...x, standard_final_volume_ml: e.target.value === '' ? null : parseInt(e.target.value) }))} /></F>
          </div>
          {ladder && <>
            <div style={{ ...hint, fontWeight: 700 }}>First infusion{unit ? ` · ${unit}` : ''}</div>
            <div style={grid4}>
              {(['rate_first_start', 'rate_first_increment', 'rate_first_interval_min', 'rate_first_max'] as const).map((k, i) => (
                <F key={k} label={['Start', 'Increase by', 'Every (min)', 'Maximum'][i]}><input style={inp} type="number" value={(c[k] as number | null) ?? ''}
                  onChange={e => setC(x => ({ ...x, [k]: e.target.value === '' ? null : Number(e.target.value) }))} /></F>
              ))}
            </div>
            <div style={{ ...hint, fontWeight: 700 }}>Subsequent infusions</div>
            <div style={grid4}>
              {(['rate_subsequent_start', 'rate_subsequent_increment', 'rate_subsequent_interval_min', 'rate_subsequent_max'] as const).map((k, i) => (
                <F key={k} label={['Start', 'Increase by', 'Every (min)', 'Maximum'][i]}><input style={inp} type="number" value={(c[k] as number | null) ?? ''}
                  onChange={e => setC(x => ({ ...x, [k]: e.target.value === '' ? null : Number(e.target.value) }))} /></F>
              ))}
            </div>
          </>}
        </SecBase>

        <SecBase {...secProps('duration', 'duration')} title="Duration and visit length" dim={!isCare} sum={visitTxt}>
          <div style={grid4}>
            <F label="Infusion duration (min)"><input style={inp} type="number" value={c.infusion_duration_min ?? ''} onChange={e => setC(x => ({ ...x, infusion_duration_min: e.target.value === '' ? null : parseInt(e.target.value) }))} /></F>
            <F label="Setup (min)"><input style={inp} type="number" value={c.setup_minutes ?? ''} onChange={e => setC(x => ({ ...x, setup_minutes: e.target.value === '' ? null : parseInt(e.target.value) }))} />{provLine('setup_minutes')}</F>
            <F label="Observation (min)"><input style={inp} type="number" value={c.observation_minutes ?? ''} onChange={e => setC(x => ({ ...x, observation_minutes: e.target.value === '' ? null : parseInt(e.target.value) }))} /></F>
            <F label="Visit length override"><input style={inp} type="number" placeholder="—" value={c.visit_length_override_min ?? ''} onChange={e => setC(x => ({ ...x, visit_length_override_min: e.target.value === '' ? null : parseInt(e.target.value) }))} /></F>
          </div>
          <div style={{ ...callout, borderLeftColor: 'var(--color-primary)', background: '#e9f7ec' }}><b>Computed visit length</b> <span style={{ fontSize: 19, fontWeight: 700, color: '#0a7a1d' }}>{visitTxt}</span></div>
        </SecBase>

        <SecBase {...secProps('constraints', 'constraints')} title="Administration constraints" dim={!isCare}
          sum={`${[c.no_iv_push, c.dedicated_line, c.protect_from_light, c.sequence_dependent, c.resume_50pct_on_reaction].filter(Boolean).length} set`}>
          <div style={cbs}>
            {([['no_iv_push', 'Do not administer as IV push or bolus'], ['dedicated_line', 'Dedicated line, no other agent'],
               ['protect_from_light', 'Protect from light'], ['sequence_dependent', 'Sequence dependent'],
               ['resume_50pct_on_reaction', 'Resume at 50% on reaction']] as const).map(([k, label]) => (
              <label key={k} style={cb}><input type="checkbox" checked={Boolean(c[k])} onChange={e => setC(x => ({ ...x, [k]: e.target.checked }))} /><span>{label}</span></label>
            ))}
          </div>
          <div style={grid2}>
            <F label="Gravity permitted">{sel(c.gravity_permitted, OPTS.gravity, v => setC(x => ({ ...x, gravity_permitted: v })))}</F>
            <F label="Gravity rationale"><input style={inp} value={c.gravity_rationale ?? ''} onChange={e => setC(x => ({ ...x, gravity_rationale: e.target.value }))} /></F>
          </div>
          <F label="Nurse watch-outs"><textarea style={{ ...inp, minHeight: 52 }} value={c.nurse_watchouts ?? ''} onChange={e => setC(x => ({ ...x, nurse_watchouts: e.target.value }))} /></F>
        </SecBase>

        <SecBase {...secProps('staffing')} title="Staffing values" dim={!isCare} sum={c.named_nurse_only ? 'named-nurse only' : 'open'}>
          <div style={grid3}>
            <F label="Named-nurse only">{sel(c.named_nurse_only ? 'Yes' : 'No', ['Yes', 'No'], v => setC(x => ({ ...x, named_nurse_only: v === 'Yes' })))}</F>
            <F label="Named nurse ids"><input style={inp} placeholder="—" value={c.named_nurse_ids ?? ''} onChange={e => setC(x => ({ ...x, named_nurse_ids: e.target.value }))} /></F>
            <div />
          </div>
        </SecBase>

        {cols.length > 0 && (
          <SecBase {...secProps('custom')} title="Custom columns" sum={`${cols.length} column${cols.length === 1 ? '' : 's'}`}>
            <div style={grid3}>
              {cols.map(cd => {
                const v = cells[cd.id]
                return (
                  <F key={cd.id} label={cd.field_name}>
                    {cd.field_type === 'checkbox'
                      ? <input type="checkbox" checked={v === true} onChange={e => onCell(cd.id, { value_bool: e.target.checked })} />
                      : cd.field_type === 'dropdown'
                        ? <SearchableSelect style={inp} value={String(v ?? '')} onChange={val => onCell(cd.id, { value_text: val })}
                            options={[{ value: '', label: '—' }, ...(cd.dropdown_options ?? '').split(',').map(o => o.trim()).filter(Boolean).map(o => ({ value: o, label: o }))]} />
                        : <input style={inp} type={cd.field_type === 'number' ? 'number' : cd.field_type === 'date' ? 'date' : 'text'} defaultValue={String(v ?? '')}
                            onBlur={e => onCell(cd.id, cd.field_type === 'number' ? { value_number: e.target.value === '' ? null : Number(e.target.value) } : cd.field_type === 'date' ? { value_date: e.target.value || null } : { value_text: e.target.value || null })} />}
                  </F>
                )
              })}
            </div>
          </SecBase>
        )}
        </fieldset>

        {/* Supply profile & generator (owner ask 2026-08-31): the supply catalog runs from inside
            the drug record too. Same endpoints as the Admin > Supply catalog tab; the AI lookup
            saves onto THIS drug's carerx_formulary row, so both surfaces read one profile. */}
        {isCare && row.id && (
          <SecBase {...secProps('supply')} title="Supply profile & pick lists"
            chipEl={<span style={{ ...chip, background: '#f4f0fe', color: '#6741d9' }}>AI from official sources only</span>}>
            <SupplySection drugReferenceId={row.id} onToast={say} />
          </SecBase>
        )}

        {/* footer */}
        <div style={{ padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: 'var(--color-surface-soft, #f7f9fa)', borderTop: '1px solid var(--color-border)' }}>
          <button style={btnGhost} onClick={() => setOpen(Object.fromEntries(['identity', 'clinical', 'assess', 'labs', 'copay', 'protocols', 'flushcon', 'rate', 'duration', 'constraints', 'staffing', 'custom', 'supply'].map(k => [k, true])))}>Expand all</button>
          <button style={btnGhost} onClick={() => setOpen({})}>Collapse all</button>
          <button style={btnGhost} onClick={() => setShowAudit(true)}>Provenance log</button>
          <span style={{ ...hint, marginLeft: 'auto' }}>{gaps.length ? 'Gaps: ' + gaps.join(' · ') : isCare ? 'All required fields set.' : 'Non-CareRx drug.'}</span>
          <button style={btnGhost} onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && <button style={{ ...btnGhost, background: 'var(--color-primary)', borderColor: 'var(--color-primary)', color: '#fff' }} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>}
        </div>

        {toast && <div style={{ position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)', background: '#1f2933', color: '#fff', padding: '9px 16px', borderRadius: 6, fontSize: 12.5, zIndex: 80 }}>{toast}</div>}

        {showAudit && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,28,34,.45)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setShowAudit(false)}>
            <div style={{ background: 'var(--color-surface, #fff)', borderRadius: 8, maxWidth: '100%', width: '100%', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', gap: 10, alignItems: 'center' }}>
                <b>Provenance log</b><span style={hint}>{prov.length} recorded action{prov.length === 1 ? '' : 's'}</span>
                <button style={{ ...btnSm, marginLeft: 'auto' }} onClick={() => setShowAudit(false)}>Close</button>
              </div>
              <div style={{ padding: '12px 16px 18px' }}>
                {prov.length === 0 ? <div style={hint}>Nothing recorded on this drug yet.</div> : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead><tr>{['Field', 'Action', 'Value', 'When'].map(h => <th key={h} style={{ textAlign: 'left', padding: '5px 8px', fontSize: 10.5, color: 'var(--color-text-soft)', textTransform: 'uppercase', borderBottom: '1px solid var(--color-border)' }}>{h}</th>)}</tr></thead>
                    <tbody>{prov.map((p, i) => (
                      <tr key={i}>
                        <td style={tdp}>{p.field}</td>
                        <td style={tdp}>{{ ai_accepted: 'Accepted from AI', ai_edited: 'AI edited', denied: 'Denied', manual: 'Manual entry' }[p.action] ?? p.action}</td>
                        <td style={tdp}>{(p.value_after ?? '').slice(0, 70)}</td>
                        <td style={tdp}>{new Date(p.acted_at).toLocaleString()}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const chip: React.CSSProperties = { display: 'inline-block', fontSize: 9.5, fontWeight: 800, borderRadius: 9, padding: '1px 6px', whiteSpace: 'nowrap', letterSpacing: .3 }
const badge: React.CSSProperties = { fontSize: 11.5, padding: '2px 9px', borderRadius: 10, fontWeight: 700 }
const inp: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 5, padding: '6px 8px', font: 'inherit', fontSize: 12.5, background: 'var(--color-surface)', width: '100%', boxSizing: 'border-box', color: 'var(--color-text)' }
const warn: React.CSSProperties = { borderColor: '#c0392b', background: '#fdf0ee' }
const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 14px' }
const grid3: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(158px, 1fr))', gap: '0 13px' }
const grid4: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0 10px' }
const hint: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-soft)', marginTop: 3 }
const cbs: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0 14px', border: '1px solid var(--color-border)', borderRadius: 5, padding: '8px 10px' }
const cb: React.CSSProperties = { display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: 12.5, padding: '2.5px 0', cursor: 'pointer' }
const callout: React.CSSProperties = { borderLeft: '3px solid #e8c97a', background: '#fdf6e3', padding: '8px 11px', borderRadius: '0 6px 6px 0', margin: '9px 0', fontSize: 12.5 }
const aip: React.CSSProperties = { border: '1px solid #d6c8f7', background: '#f4f0fe', borderRadius: 6, padding: '9px 11px', margin: '9px 0' }
const aih: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: .5, color: '#6741d9' }
const sug: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid #d6c8f7', borderRadius: 5, padding: '7px 9px', marginBottom: 6, display: 'flex', gap: 9, alignItems: 'flex-start', flexWrap: 'wrap' }
const ln: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px dashed var(--color-border)', fontSize: 12.5 }
const srcTag: React.CSSProperties = { fontSize: 9.5, borderRadius: 3, padding: '0 5px', marginLeft: 5, whiteSpace: 'nowrap', background: '#eef2fd', color: '#3b5bdb', border: '1px solid #c6d2f7' }
const btnSm: React.CSSProperties = { font: 'inherit', fontSize: 11, padding: '3px 8px', borderRadius: 5, border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer', color: 'var(--color-text)', fontWeight: 600 }
const btnGhost: React.CSSProperties = { font: 'inherit', fontSize: 12.5, fontWeight: 600, borderRadius: 5, padding: '6px 12px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer', color: 'var(--color-text)' }
const tdp: React.CSSProperties = { padding: '5px 8px', borderBottom: '1px solid var(--color-border)' }

// ── Why these live out here ────────────────────────────────────────────────────────────────────
// They used to be declared INSIDE the editor component. A component defined in a render body is a
// brand-new type on every render, so React unmounts and remounts its whole subtree each time state
// changes — which threw the page back to the top on every checkbox tick, and dropped focus mid-typing.
// Hoisted to module scope they keep their identity, so a state change re-renders in place.
interface SecProps {
  id: string; title: string; chipEl?: React.ReactNode; sum?: string; aiKey?: string; dim?: boolean
  children: React.ReactNode
  isOpen: boolean; onToggle: () => void
  aiBusy: string | null; onAI: (sec: string) => void | Promise<void>
  panel: React.ReactNode
}
function SecBase({ title, chipEl, sum, aiKey, dim, children, isOpen, onToggle, aiBusy, onAI, panel }: SecProps) {
  return (
    <div style={{ borderBottom: '1px solid var(--color-border)', opacity: dim ? .45 : 1 }}>
      <div onClick={onToggle}
        style={{ padding: '10px 16px', display: 'flex', gap: 9, alignItems: 'center', cursor: 'pointer', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--color-text-soft)', transform: isOpen ? 'rotate(90deg)' : undefined, transition: 'transform .15s' }}>▶</span>
        <b style={{ fontSize: 12.5, textTransform: 'uppercase', letterSpacing: .6, color: '#8a6d1f' }}>{title}</b>
        {chipEl}
        {aiKey && <button style={{ ...btnSm, background: '#6741d9', borderColor: '#6741d9', color: '#fff' }} disabled={aiBusy !== null}
          onClick={e => { e.stopPropagation(); void onAI(aiKey) }}>{aiBusy === aiKey ? 'Asking…' : 'AI suggest'}</button>}
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--color-text-soft)' }}>{sum}</span>
      </div>
      {isOpen && <div style={{ padding: '2px 16px 16px' }}>{panel}{children}</div>}
    </div>
  )
}
function F({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#8a6d1f', marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  )
}

/**
 * Supply profile & pick lists inside the drug record (owner ask 2026-08-31). Resolves this drug's
 * carerx_formulary row through drug_reference_id, then drives the same endpoints as Admin > Supply
 * catalog: AI lookup (allow-listed official sources, citations shown), accept → drug profile,
 * approve (the SUP-08/SUP-10 gates run server-side), a quick generator, and saved-list history.
 */
function SupplySection({ drugReferenceId, onToast }: { drugReferenceId: string; onToast: (m: string) => void }) {
  const [drug, setDrug] = useState<api.SupplyDrug | null>(null)
  const [pumps, setPumps] = useState<api.SupplyPumpSet[]>([])
  const [notLinked, setNotLinked] = useState(false)
  const [ai, setAi] = useState<api.SupplyAiLookup | null>(null)
  const [aiBusy, setAiBusy] = useState(false)
  const [gen, setGen] = useState<api.PickListResult | null>(null)
  const [genBusy, setGenBusy] = useState(false)
  const [history, setHistory] = useState<api.PickListHistoryRow[]>([])
  const [msg, setMsg] = useState('')
  const [access, setAccess] = useState('peripheral')
  const [site, setSite] = useState('home')
  const [pump, setPump] = useState('CURLIN 6000')

  const reload = useCallback(async () => {
    try {
      const cat = await api.getCareRxSupplyCatalog()
      const mine = cat.drugs.find(d => d.drug_reference_id === drugReferenceId) ?? null
      setDrug(mine); setPumps(cat.pump_sets); setNotLinked(!mine)
      if (mine) {
        const [past, hist] = await Promise.all([
          api.listCareRxSupplyAiLookups(mine.formulary_id),
          api.listCareRxPickLists(mine.formulary_id),
        ])
        setAi(past.items.find(l => l.status === 'ok') ?? null)
        setHistory(hist.items)
      }
    } catch { setNotLinked(true) }
  }, [drugReferenceId])
  useEffect(() => { void reload() }, [reload])

  if (notLinked) return <div style={hint}>This drug record is not linked to a CareRx formulary row, so no supply profile exists for it.</div>
  if (!drug) return <div style={hint}>Loading supply profile…</div>

  const isIv = (drug.supply_route ?? 'IV') === 'IV'

  const runAi = async () => {
    setAiBusy(true); setMsg('')
    try { setAi(await api.runCareRxSupplyAiLookup({ formulary_id: drug.formulary_id })); onToast('Lookup complete — review the citations') }
    catch (e: any) { setMsg(e?.message ?? 'lookup failed') } finally { setAiBusy(false) }
  }
  const acceptAi = async () => {
    if (!ai) return
    setAiBusy(true); setMsg('')
    try {
      const r = await api.acceptCareRxSupplyAiLookup(ai.id)
      onToast(`Saved to drug profile: ${r.accepted.length} fields, ${r.citations_saved} citations`)
      setAi({ ...ai, accepted_at: new Date().toISOString() }); void reload()
    } catch (e: any) { setMsg(e?.message ?? 'accept failed') } finally { setAiBusy(false) }
  }
  const approve = async () => {
    setMsg('')
    try {
      await api.approveCareRxSupplyProfile(drug.formulary_id, { approve: !drug.approved })
      onToast(drug.approved ? 'Supply profile un-approved' : 'Supply profile approved'); void reload()
    } catch (e: any) {
      // SUP-10: the server demands a written pharmacist reason for an IV profile with no rescue kit.
      if (String(e?.message ?? '').includes('rescue_override_reason_required')) {
        const reason = prompt('This IV profile has no rescue kit. A written pharmacist override reason is required (it prints on every checklist):')
        if (!reason) return
        try { await api.approveCareRxSupplyProfile(drug.formulary_id, { approve: true, rescue_override_reason: reason }); onToast('Supply profile approved with override'); void reload() }
        catch (e2: any) { setMsg(e2?.message ?? 'approve failed') }
      } else setMsg(e?.message ?? 'approve failed')
    }
  }
  const generate = async () => {
    setGenBusy(true); setMsg('')
    try {
      setGen(await api.generateCareRxPickList({
        formulary_id: drug.formulary_id, access, site_of_care: site,
        pump: isIv ? pump : undefined, pack_level: drug.default_pack_level ?? 'standard',
      }))
      void reload()
    } catch (e: any) { setMsg(e?.message ?? 'generate failed') } finally { setGenBusy(false) }
  }

  const pumpNames = Array.from(new Set(pumps.map(p => p.pump)))
  const softChip: React.CSSProperties = { ...chip, background: 'var(--color-surface-soft, #f2f5f7)', color: 'var(--color-text-soft, #6b7280)' }
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        {drug.approved
          ? <span style={{ ...chip, background: '#e9f7ec', color: '#0a7a1d' }}>profile approved</span>
          : <span style={{ ...chip, background: '#fdf6e3', color: '#8a6d1f' }}>profile not approved — generator blocked</span>}
        {drug.supply_route && <span style={softChip}>route {drug.supply_route}</span>}
        {drug.diluent && <span style={softChip}>{drug.diluent}{drug.standard_final_volume_ml ? ` · ${drug.standard_final_volume_ml} mL` : ''}</span>}
        <button style={{ ...btnSm, background: '#6741d9', borderColor: '#6741d9', color: '#fff' }} disabled={aiBusy} onClick={() => void runAi()}>
          {aiBusy ? 'Looking up…' : ai ? 'Re-run AI supply lookup' : 'AI supply lookup'}</button>
        {ai?.status === 'ok' && !ai.accepted_at && <button style={btnSm} disabled={aiBusy} onClick={() => void acceptAi()}>Save to drug profile</button>}
        <button style={btnSm} onClick={() => void approve()}>{drug.approved ? 'Un-approve' : 'Approve profile'}</button>
      </div>
      {msg && <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 6 }}>{msg}</div>}

      {ai?.fields && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 4 }}>
            AI proposal {ai.accepted_at ? '· saved to profile' : '· not saved yet'} · every value cites an official source
          </div>
          {Object.entries(ai.fields).map(([field, value]) => {
            const cites = (ai.citations ?? []).filter(cx => cx.field === field)
            return (
              <div key={field} style={{ ...ln, borderBottom: '1px dashed var(--color-border)' }}>
                <span style={{ minWidth: 170, fontWeight: 600 }}>{field}</span>
                <span style={{ flex: 1 }}>
                  {typeof value === 'object' ? JSON.stringify(value).slice(0, 90) : String(value)}
                  {cites.map((cit, i) => (
                    <div key={i} style={{ fontSize: 11 }}>
                      <a href={cit.url} target="_blank" rel="noreferrer" style={{ color: '#3b5bdb' }}>{cit.title ?? cit.url}</a>
                      <span style={srcTag}>{cit.source_tier}</span>
                      {cit.quoted_text && <div style={{ ...hint, fontStyle: 'italic' }}>&ldquo;{cit.quoted_text}&rdquo;</div>}
                    </div>
                  ))}
                </span>
              </div>
            )
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap', marginBottom: 8 }}>
        <F label="Access"><SearchableSelect style={{ ...inp, minWidth: 120 }} value={access} onChange={setAccess}
          options={['peripheral', 'port', 'picc', 'midline'].map(a => ({ value: a, label: a }))} /></F>
        <F label="Site of care"><SearchableSelect style={{ ...inp, minWidth: 110 }} value={site} onChange={setSite}
          options={[{ value: 'home', label: 'home' }, { value: 'aic', label: 'AIC / AIS' }]} /></F>
        {isIv && <F label="Pump (this visit)"><SearchableSelect style={{ ...inp, minWidth: 150 }} value={pump} onChange={setPump}
          options={pumpNames.map(n => ({ value: n, label: n, disabled: n === 'Gravity' && drug.gravity_permitted === 'no', hint: n === 'Gravity' && drug.gravity_permitted === 'no' ? 'not permitted' : undefined }))} /></F>}
        <button style={{ ...btnSm, marginBottom: 10 }} disabled={genBusy || !drug.approved} onClick={() => void generate()}>
          {genBusy ? 'Generating…' : 'Generate pick list'}</button>
      </div>

      {gen && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 10px', marginBottom: 10, fontSize: 12 }}>
          <b>{gen.state === 'complete' ? '✓ complete' : '⚠ incomplete'}</b> · {gen.count} items · est. ${gen.total_cost.toFixed(2)}
          {gen.unmet.length > 0 && <span> · {gen.unmet.length} not in catalog: {gen.unmet.map(u => u.item_name).join(', ')}</span>}
        </div>
      )}

      {history.length > 0 && (
        <div style={hint}>
          {history.length} saved list{history.length === 1 ? '' : 's'} for this drug — latest {new Date(history[0].generated_at).toLocaleDateString()} ({history[0].state}). Open them under CareRx → Admin → Supply catalog → History.
        </div>
      )}
    </div>
  )
}
